import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppShell from './shell/AppShell';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { UpdateAvailableBanner } from './components/UpdateAvailableBanner';

// ── Eagerly-loaded routes ────────────────────────────────────────────────
// MapPage is the default landing surface; everything else is code-split.
import MapPage from './routes/MapPage';
import AuthCallback from './routes/AuthCallback';
import SupabaseAuthCallback from './routes/SupabaseAuthCallback';
import SupabaseAuthReset from './routes/SupabaseAuthReset';

// ── Lazy-loaded routes ───────────────────────────────────────────────────
// Each route becomes its own chunk and is only fetched/parsed when the
// pilot navigates there. Cuts the main bundle from ~3.55 MB → ~1.8-2.0 MB
// (audit 2026-05-22). Critical for first-paint on tarmac LTE.
//
// MapPage and the OAuth callbacks stay eager because:
//   - MapPage is the entry point (always rendered on cold start)
//   - Auth callbacks fire mid-navigation and can't tolerate a Suspense
//     fallback flash
const AirportsPage = lazy(() => import('./routes/AirportsPage'));
const PlatesPage = lazy(() => import('./routes/PlatesPage'));
const MorePage = lazy(() => import('./routes/MorePage'));
const AircraftPage = lazy(() => import('./routes/AircraftPage'));
const TrackLogsPage = lazy(() => import('./routes/TrackLogsPage'));
const SettingsPage = lazy(() => import('./routes/SettingsPage'));
const FlightsPage = lazy(() => import('./routes/FlightsPage'));
const LogbookPage = lazy(() => import('./routes/LogbookPage'));
const DocumentsPage = lazy(() => import('./routes/DocumentsPage'));
const IncidentsPage = lazy(() => import('./routes/IncidentsPage'));
// IncidentDetailPage is currently imported via IncidentsPage; keep grouped.
const CrewPage = lazy(() => import('./routes/CrewPage'));
// CrewDetailPage handled via CrewPage routing.
const FratPage = lazy(() => import('./routes/FratPage'));
const FratNewPage = lazy(() => import('./routes/FratNewPage'));
const FratDetailPage = lazy(() => import('./routes/FratDetailPage'));
const RoleManagementPage = lazy(() => import('./routes/RoleManagementPage'));
const FlightBriefingPage = lazy(() => import('./routes/FlightBriefingPage'));
const PlateCorrectionsAdminPage = lazy(
  () => import('./routes/PlateCorrectionsAdminPage'),
);
const PlateReviewQueuePage = lazy(
  () => import('./routes/PlateReviewQueuePage'),
);
const DebriefPage = lazy(() => import('./routes/DebriefPage'));
const MyCorrectionsPage = lazy(() => import('./routes/MyCorrectionsPage'));

// Lightweight fallback shown while a lazy route chunk downloads.
// Matches the splash-screen dark background so route transitions
// don't flash white. Spinner kept minimal — full-screen flashes are
// disorienting in a cockpit.
function RouteFallback() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: '#0b0f14',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 5,
      }}
      aria-busy="true"
      aria-label="Loading"
    >
      <div
        style={{
          width: 28,
          height: 28,
          border: '2px solid #2d3947',
          borderTopColor: '#7cd1ff',
          borderRadius: '50%',
          animation: 'fyrefly-route-spin 0.9s linear infinite',
        }}
      />
      <style>{`@keyframes fyrefly-route-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function App() {
  return (
    <AppShell>
      <UpdateAvailableBanner />
      <RouteErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to="/map" replace />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/airports" element={<AirportsPage />} />
          <Route path="/airports/:ident" element={<AirportsPage />} />
          <Route path="/plates" element={<PlatesPage />} />
          <Route path="/flights" element={<FlightsPage />} />
          <Route path="/flights/:id" element={<FlightsPage />} />
          {/* Weather briefing — full-window, AppShell-bypassed. Wiley
              requested 2026-05-12: ForeFlight-style printable briefing
              that opens in a new tab. */}
          <Route path="/flights/:id/briefing" element={<FlightBriefingPage />} />
          <Route path="/logbook" element={<LogbookPage />} />
          <Route path="/auth/supabase/callback" element={<SupabaseAuthCallback />} />
          <Route path="/auth/supabase/reset" element={<SupabaseAuthReset />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/aircraft" element={<AircraftPage />} />
          <Route path="/incidents" element={<IncidentsPage />} />
          <Route path="/incidents/:id" element={<IncidentsPage />} />
          <Route path="/crew" element={<CrewPage />} />
          <Route path="/crew/:id" element={<CrewPage />} />
          <Route path="/frat" element={<FratPage />} />
          <Route path="/frat/new" element={<FratNewPage />} />
          <Route path="/frat/:id" element={<FratDetailPage />} />
          <Route path="/tracks" element={<TrackLogsPage />} />
          <Route path="/tracks/:id" element={<TrackLogsPage />} />
          <Route path="/debrief/:trackId" element={<DebriefPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/more" element={<MorePage />} />
          <Route path="/admin/roles" element={<RoleManagementPage />} />
          <Route path="/admin/plate-corrections" element={<PlateCorrectionsAdminPage />} />
          <Route path="/admin/plate-corrections/review" element={<PlateReviewQueuePage />} />
          <Route path="/me/plate-corrections" element={<MyCorrectionsPage />} />
          <Route path="/auth/agol/callback" element={<AuthCallback />} />
          <Route path="*" element={<Navigate to="/map" replace />} />
        </Routes>
      </Suspense>
      </RouteErrorBoundary>
    </AppShell>
  );
}
